import type { EmailBrandingKind } from "services/types/email-branding.type";

import EmailBrandingBanner from "./email-branding-banner.component";
import { type EmailBrandingLinksProps } from "./email-branding-links.component";
import {
  DESIGNER_SENT_AS_DESIGNED_LINES,
  TODAYS_PARTS,
  describeBrandingBanner
} from "./email-branding.utils";
import { useEmailBranding } from "./use-email-branding.hook";

interface EmailBrandingNoticeProps extends EmailBrandingLinksProps {
  /**
   * The kind this email is branded as. Undefined: a type in no kind, which is branded as today.
   * "any": a reusable template, whose kind is only known when it is sent.
   */
  kind: EmailBrandingKind | "any" | undefined;
  /** The email is a Designer document, which never gets the logo. */
  isDesigner?: boolean;
}

/** Read-only: where branding is decided by the practice setting for a kind, not per email. */
export default function EmailBrandingNotice({
  kind,
  isDesigner = false,
  getPreviewRequest,
  zIndex
}: EmailBrandingNoticeProps) {
  const { branding, isSupported, hasSavedSettings } = useEmailBranding();

  if (!isSupported) return null;

  const parts =
    kind === "any"
      ? undefined
      : kind
        ? branding.categories[kind]
        : TODAYS_PARTS;

  // A type in no kind keeps today's parts, which the backend never adds to a Designer email.
  const lines =
    isDesigner && kind === undefined
      ? [...DESIGNER_SENT_AS_DESIGNED_LINES]
      : describeBrandingBanner({ parts, isDesigner, hasSavedSettings });

  return (
    <EmailBrandingBanner
      lines={lines}
      // A Designer body lives in the builder, not in the form field the preview reads, and a
      // reusable template has no kind to preview against.
      getPreviewRequest={
        isDesigner || kind === "any" ? undefined : getPreviewRequest
      }
      zIndex={zIndex}
    />
  );
}
